/* global data, config */
import CloseIcon from "@mui/icons-material/Close";
import { IconButton } from "@mui/material";
import { SnackbarProvider, closeSnackbar } from "notistack";
import {
  Outlet,
  RouterProvider,
  createBrowserRouter,
  redirect,
  useNavigate,
  Navigate,
} from "react-router-dom";

// Project imports
import adminRouteObj from "./components/AdminLayout";
import syncScheduleRouteObj from "./views/SyncSchedulePage";
import CostManagementPage from "./views/CostManagement";

import FullScreenLoader from "./components/FullScreenLoader";
import projectRootRouteObj from "./components/MainLayout";
import { LayoutProvider } from "./contexts/LayoutContext";
import {
  getLoggedInUserWithCustomClaims,
  getLoggedInUser,
} from "./helpers/auth";
import ThemeCustomization from "./themes";
import loginPageRouteObj from "./views/LoginPage";
import forgotPwPageRouteObj from "./views/ForgotPasswordPage";
import resetPwPageRouteObj from "./views/ResetPasswordPage";
import managedSyncsRouteObj from "./views/ManagedSyncsIndexPage";
import matchingJobsRouteObj from "./views/MatchingJobsIndexPage";
import newMatchingJobsRouteObj from "./views/NewMatchingJobPage";
import listCutterJobsRouteObj from "./views/ListCutter/ListCutterJobsIndexPage";
import newListCutterJobRouteObj from "./views/ListCutter/NewListCutterJobPage";
import listCutterJobDetailsRouteObj from "./views/ListCutter/ListCutterJobDetails";
import crosstabsIndexRouteObj from "./views/Crosstabs/CrosstabsIndexPage";
import newCrosstabRouteObj from "./views/Crosstabs/NewCrosstabPage";
import budgetIndexRouteObj from "./views/Budget/BudgetIndexPage";
import hartIndexRouteObj from "./views/Hart/HartIndexPage";
import hartAdminRouteObj from "./views/Hart/admin/HartAdminPage";
import hartDataRestrictionFormRouteObj from "./views/Hart/admin/pages/DataRestrictionFormPage";
import hartAddNewRouteObj from "./views/Hart/pages/AddNewPage";
import hartAddNewDetailsRouteObj from "./views/Hart/pages/AddNewDetailsPage";
import hartJobDetailsRouteObj from "./views/Hart/pages/JobDetailsPage";
import hartClientDetailsRouteObj from "./views/Hart/pages/ClientDetailsPage";
import hartClientContactDetailsRouteObj from "./views/Hart/pages/ClientContactDetailsPage";
import hartAnalystDetailsRouteObj from "./views/Hart/pages/AnalystDetailsPage";
import fileUploaderRouteObj from "./views/FileUploader/pages/FileUploaderPage";
import fileUploaderAdminRouteObj from "./views/FileUploader/admin/FileUploaderAdminPage";
import createEditTemplateRouteObj from "./views/FileUploader/admin/pages/CreateEditTemplatePage";
import editSchemaRouteObj from "./views/FileUploader/admin/pages/EditSchemaPage";
import hartSessionDetailsRouteObj from "./views/Hart/pages/SessionDetailsPage";
import hartVendorDetailsRouteObj from "./views/Hart/pages/VendorDetailsPage";
import hartFacilityDetailsRouteObj from "./views/Hart/pages/FacilityDetailsPage";
import HartPageWrapper from "./views/Hart/HartPageWrapper";
import registryAdminRouteObj from "./views/Registry/admin/RegistryAdminPage";
import registryOrgFormRouteObj from "./views/Registry/admin/pages/OrganizationFormPage";
import registryDashboardRouteObj from "./views/Registry/RegistryDashboardPage";
import registryWizardRouteObj from "./views/Registry/wizard/RegistryWizardPage";
import registryDetailRouteObj from "./views/Registry/RegistryExperimentDetailPage";
import RegistryPageWrapper from "./views/Registry/RegistryPageWrapper";

import welcomePageRouteObj from "./views/WelcomePage";
import projectWelcomeRouteObj from "./views/ProjectWelcomePage";
import buildRoute from "./components/buildRoute";
import ErrorBoundary from "./components/ErrorBoundary";
import AnalyticsConsentBanner from "./components/AnalyticsConsentBanner";

const router = createBrowserRouter([
  {
    path: "/",
    id: "root",
    Component: () => {
      const navigate = useNavigate();
      return <Outlet />;
    },
    children: [
      {
        index: true,
        loader: async () => redirect("/projects"),
      },
      {
        path: "login",
        ...loginPageRouteObj,
      },
      {
        path: "forgot-password",
        ...forgotPwPageRouteObj,
      },
      {
        path: "reset-password",
        ...resetPwPageRouteObj,
      },
      {
        path: "welcome",
        errorElement: <ErrorBoundary />,
        ...welcomePageRouteObj,
      },
      {
        path: "admin",
        id: "admin",
        ...adminRouteObj,
        children: [
          {
            path: "sync-schedule",
            ...syncScheduleRouteObj,
          },
          {
            path: "cost-management",
            element: <CostManagementPage />,
          },
        ],
      },
      {
        path: "projects",
        id: "projects",
        loader: async () => {
          const loggedInUser = await getLoggedInUserWithCustomClaims();
          return { loggedInUser };
        },
        errorElement: <ErrorBoundary />,
        children: [
          {
            index: true,
            loader: async () => {
              const user = await getLoggedInUser();

              const allPartnersForUser = user?.partners;

              // Sort partners alphabetically by slug to ensure consistent default project
              const sortedPartners = allPartnersForUser?.sort((a, b) =>
                (a.slug || '').localeCompare(b.slug || '')
              );

              const defaultPartner = sortedPartners?.[0];
              const defaultProjectId = defaultPartner?.gcp_project_id;

              // If user belongs to no partners
              if (allPartnersForUser?.length === 0 || !defaultProjectId) {
                return redirect("/welcome");
              }

              return redirect(`/projects/${defaultProjectId}`);
            },
          },
          {
            path: ":projectId",
            id: "projectRoot",
            ...projectRootRouteObj,
            children: [
              {
                index: true,
                ...projectWelcomeRouteObj,
              },
              {
                path: "syncs",
                ...buildRoute({}),
                children: [
                  {
                    path: "managed",
                    ...managedSyncsRouteObj,
                  },
                ],
              },
              {
                path: "matching-jobs",
                ...matchingJobsRouteObj,
              },
              {
                path: "matching-jobs/new",
                ...newMatchingJobsRouteObj,
              },
              {
                path: "list-cutter",
                children: [
                  {
                    index: true,
                    ...listCutterJobsRouteObj,
                  },
                  {
                    path: "new",
                    ...newListCutterJobRouteObj,
                  },
                  {
                    path: ":jobId",
                    ...listCutterJobDetailsRouteObj,
                  }
                ],
              },
              {
                path: "crosstabs",
                children: [
                  { index: true, ...crosstabsIndexRouteObj },
                  {
                    path: "new",
                    children: [
                      { index: true, element: <Navigate to="step1" replace /> },
                      { path: "step1", ...newCrosstabRouteObj },
                      { path: "step2", ...newCrosstabRouteObj },
                      { path: "step3", ...newCrosstabRouteObj },
                      { path: "step4", ...newCrosstabRouteObj },
                      { path: "step5", ...newCrosstabRouteObj },
                      { path: "step6", ...newCrosstabRouteObj },
                    ],
                  },
                ],
              },
              {
                path: "budget",
                children: [
                  { index: true, ...budgetIndexRouteObj },
                ],
              },
              {
                path: "hart",
                ...buildRoute({ element: <HartPageWrapper /> }),
                children: [
                  { index: true, ...hartIndexRouteObj },
                  { path: "admin", ...hartAdminRouteObj },
                  { path: "admin/data-restrictions/create", ...hartDataRestrictionFormRouteObj },
                  { path: "admin/data-restrictions/:firewallId/edit", ...hartDataRestrictionFormRouteObj },
                  { path: "new", ...hartAddNewRouteObj },
                  { path: "new/:tableType", ...hartAddNewDetailsRouteObj },
                  { path: "jobs/:jobNo", ...hartJobDetailsRouteObj },
                  { path: "clients/:clientId", ...hartClientDetailsRouteObj },
                  { path: "client_contacts/:clientContactId", ...hartClientContactDetailsRouteObj },
                  { path: "analysts/:analystId", ...hartAnalystDetailsRouteObj },
                  { path: "sessions/:sessionId", ...hartSessionDetailsRouteObj },
                  { path: "vendors/:vendorId", ...hartVendorDetailsRouteObj },
                  { path: "facilities/:facilityId", ...hartFacilityDetailsRouteObj },
                ],
              },
              {
                path: "registry",
                ...buildRoute({ element: <RegistryPageWrapper /> }),
                children: [
                  { path: "admin", ...registryAdminRouteObj },
                  { path: "admin/organizations/create", ...registryOrgFormRouteObj },
                  { path: "admin/organizations/:orgId/edit", ...registryOrgFormRouteObj },
                  { index: true, ...registryDashboardRouteObj },
                  {
                    path: ":experimentId/edit",
                    children: [
                      { index: true, element: <Navigate to="step1" replace /> },
                      { path: "step1", ...registryWizardRouteObj },
                      { path: "step2", ...registryWizardRouteObj },
                      { path: "step3", ...registryWizardRouteObj },
                      { path: "step4", ...registryWizardRouteObj },
                      { path: "step5", ...registryWizardRouteObj },
                      { path: "step6", ...registryWizardRouteObj },
                    ],
                  },
                  { path: ":experimentId", ...registryDetailRouteObj },
                ],
              },
              {
                path: "file-uploader",
                ...buildRoute({}),
                children: [
                  { index: true, ...fileUploaderRouteObj },
                  { path: "admin", ...fileUploaderAdminRouteObj },
                  { path: "admin/templates/new", ...createEditTemplateRouteObj },
                  { path: "admin/templates/:templateId/edit", ...createEditTemplateRouteObj },
                  { path: "admin/schemas/:schemaId/edit", ...editSchemaRouteObj },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
]);

export default function App() {
  return (
    <ThemeCustomization>
      <LayoutProvider>
        <SnackbarProvider
          anchorOrigin={{
            vertical: "top",
            horizontal: "center",
          }}
          action={(snackbarId) => (
            <IconButton
              size="small"
              aria-label="close"
              color="inherit"
              onClick={() => closeSnackbar(snackbarId)}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        >
          <RouterProvider
            router={router}
            fallbackElement={<FullScreenLoader />}
          />
          <AnalyticsConsentBanner />
        </SnackbarProvider>
      </LayoutProvider>
    </ThemeCustomization>
  );
}
